import { useEffect, useState, useCallback, useRef } from 'react';

import { useMerchant } from '@contexts/admin/MerchantContext';
import { message } from 'antd';

import adminCable from '@/lib/adminCable';
import i18n from '@/i18n/i18n';

// 翻譯訊息（如果是 i18n key 則翻譯，否則直接顯示）
const translateMessage = (msg, params = {}) => {
  if (!msg) return '';
  // 檢查是否為 i18n key 格式（包含點號且不含空格）
  if (msg.includes('.') && !msg.includes(' ')) {
    return i18n.t(msg, params);
  }
  return msg;
};

/**
 * Admin 專用的即時通知 Hook
 *
 * 功能：
 * - 自動訂閱 NotificationChannel
 * - 接收 job 狀態更新和系統通知
 * - 顯示 Ant Design message 提示
 * - 自動清理訂閱（防止 fd 洩漏）
 *
 * @returns {Object} { notificationData, clearJobStatus, setModalVisible }
 *
 * 使用範例：
 * ```javascript
 * function ImportPage() {
 *   const { notificationData } = useNotification();
 *
 *   const handleImport = async () => {
 *     const res = await adminApiFetch('/admin_api/v1/import', { method: 'POST' });
 *     message.info(`任務已開始 (Job ID: ${res.data.job_id})`);
 *   };
 *
 *   // jobStatus 會在收到 WebSocket 通知時自動更新
 *   useEffect(() => {
 *     if (jobStatus?.status === 'completed') {
 *       console.log('Job completed:', jobStatus);
 *     }
 *   }, [jobStatus]);
 *
 *   return <Button onClick={handleImport}>開始匯入</Button>;
 * }
 * ```
 */
// 進度通知節流：long-running worker 會以 ~25 Hz 推送 progress（broadcast_progress），
// 每筆都進 setNotificationData → AdminNotificationContext 值變動 → 整個 admin 子樹高頻 re-render。
// dev StrictMode 雙倒 render 下，某些下游 inline-ref 反模式（如 Table 分頁 Select）會在一個 commit
// cycle 內疊到 React 巢狀 setState 上限，丟 Maximum update depth exceeded。
// 對「非終止狀態」事件 coalesce per 100ms by job_id（最後一筆贏）；
// 終止狀態（completed/failed）即時寫入並先 flush 暫存，保證最終狀態看到正確值。
// （與 operator 對應 hook 同步邏輯。）
const TERMINAL_JOB_STATUSES = new Set([ 'completed', 'failed' ]);

export function useNotification() {
  const { currentMerchant, loading } = useMerchant();
  const [notificationData, setNotificationData] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const modalVisibleRef = useRef(false);
  const unsubscribeRef = useRef(null);  // 🔥 儲存 unsubscribe 函數
  const currentMerchantIdRef = useRef(null); // 🔥 儲存當前的 merchant_id

  // 節流用 buffer + timer（同 operator）
  const pendingProgressRef = useRef({});
  const flushTimerRef = useRef(null);

  const flushPendingProgress = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const pending = pendingProgressRef.current;
    if (Object.keys(pending).length === 0) return;
    pendingProgressRef.current = {};
    setNotificationData((prev) => ({ ...prev, ...pending }));
  }, []);

  // 🔥 [SessionStorage] 使用 sessionStorage 確保 Reload 後仍記得是此 Tab 發起的
  const JOB_OWNERSHIP_KEY = 'gw_admin_my_jobs';

  const getMyJobs = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(JOB_OWNERSHIP_KEY);
      return new Set(stored ? JSON.parse(stored) : []);
    } catch (e) {
      console.warn('Failed to parse job ownership', e);
      return new Set();
    }
  }, []);

  const addMyJob = useCallback((jobId) => {
    try {
      const current = getMyJobs();
      current.add(String(jobId));
      // 限制數量避免無限膨脹，保留最近 50 筆
      const limited = Array.from(current).slice(-50);
      sessionStorage.setItem(JOB_OWNERSHIP_KEY, JSON.stringify(limited));
    } catch (e) {
      console.warn('Failed to save job ownership', e);
    }
  }, [getMyJobs]);

  /**
   * 註冊一個 Job ID 為「本 Tab 發起」
   * 在呼叫 API 成功取得 job_id 後立即呼叫此方法
   */
  const registerJob = useCallback((jobId) => {
    if (jobId) {
      console.log('[Admin useNotification] 🏷️ Registered job ownership (Persisted):', jobId);
      addMyJob(jobId);
    }
  }, [addMyJob]);

  // 同步 modalVisible 到 ref
  useEffect(() => {
    modalVisibleRef.current = modalVisible;
  }, [modalVisible]);

  useEffect(() => {
    const newMerchantId = currentMerchant?.id;

    console.log('[Admin useNotification] ⚡ useEffect triggered', {
      prevMerchantId: currentMerchantIdRef.current,
      newMerchantId,
      hasMerchant: !!currentMerchant
    });

    // ✅ 如果沒有 merchant ID，清理舊訂閱並返回
    if (!newMerchantId) {
      console.log('[Admin useNotification] ❌ No merchant ID, skipping subscription');

      // 清理舊訂閱
      if (unsubscribeRef.current) {
        console.log('[Admin useNotification] 🧹 Cleaning up old subscription (no merchant)');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      currentMerchantIdRef.current = null;
      return;
    }

    // 🔥 如果 merchant_id 沒有改變，不重新訂閱
    if (currentMerchantIdRef.current === newMerchantId && unsubscribeRef.current) {
      console.log('[Admin useNotification] ✅ Merchant ID unchanged, keeping existing subscription');
      return;
    }

    // 🔥 如果 merchant_id 改變了，先清理舊訂閱
    if (unsubscribeRef.current) {
      console.log('[Admin useNotification] 🧹 Cleaning up old subscription (merchant changed)');
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // 🔥 訂閱新的 merchant
    console.log('[Admin useNotification] 🚀 Subscribing to NotificationChannel with merchant_id:', newMerchantId);
    currentMerchantIdRef.current = newMerchantId;

    const unsubscribe = adminCable.subscribe(
      'NotificationChannel',
      { merchant_id: newMerchantId },
      {
        received: (data) => {
          console.log('[Admin useNotification] ✅ Received data:', data);

          // ✅ 根據 type 處理不同類型的訊息
          if (data.type === 'job_status') {
            // [修正] 從 SessionStorage 讀取判斷
            const myJobs = getMyJobs();
            const isOriginator = myJobs.has(String(data.job_id));
            const enrichedData = { ...data, isOriginator };

            // Background Job 狀態更新 — 節流見頂部 TERMINAL_JOB_STATUSES 註解。
            if (TERMINAL_JOB_STATUSES.has(data.status)) {
              // 終止狀態優先：先 flush 之前 coalesce 的 progress（確保 progress final 值不會壓過 completed/failed），
              // 再把終止資料寫入。flush+set 兩次 setState 會被 React 自動 batch 成一個 commit。
              flushPendingProgress();
              setNotificationData((prev) => ({ ...prev, [data.job_id]: enrichedData }));
            } else {
              // processing / started 等非終止狀態：100ms coalesce by job_id（最後一筆贏）
              pendingProgressRef.current[data.job_id] = enrichedData;
              if (!flushTimerRef.current) {
                flushTimerRef.current = setTimeout(flushPendingProgress, 100);
              }
            }

            // 只在 Modal 關閉時才顯示通知
            if (!modalVisibleRef.current) {
              switch (data.status) {
                case 'completed':
                  // 匯入完成由 BackgroundJobManager 用 notification.success() 顯示
                  if (!data.data?.import_id) {
                    message.success(translateMessage(data.message, data.progress) || i18n.t('background_job.messages.complete'));
                  }
                  break;
                case 'failed':
                  // 失敗由 BackgroundJobManager 用 notification.error() 顯示（持久通知）
                  break;
                case 'started':
                case 'processing':
                  console.log('[Job Progress]', data.job_id, data.message, data.progress);
                  break;
                default:
                  console.log('Unknown job status:', data.status);
              }
            } else {
              console.log('[Job Status Update]', data.status, data.job_id, data.message);
            }
          } else if (data.type === 'new_notification') {
            // 系統通知（Notification）
            // 這個會由 NotificationBell 組件處理
            console.log('[Admin useNotification] 📬 New notification:', data.notification);
            setNotificationData((prev) => ({
              ...prev,
              [`notification_${Date.now()}`]: data
            }));
          }
        },
        connected: () => {
          console.log('[Admin useNotification] ✅ Connected to NotificationChannel');
        },
        disconnected: () => {
          console.log('[Admin useNotification] ⚠️ Disconnected from NotificationChannel');
          // 🔥 不需要手動重連，ActionCable 會自動處理
          // 當連接恢復時，connected() 會被再次觸發
        },
        rejected: () => {
          console.error('[Admin useNotification] ❌ Subscription rejected');
          message.error('無法連接到通知服務');
        }
      }
    );

    unsubscribeRef.current = unsubscribe;

    // ✅ Cleanup：組件 unmount 時清理訂閱 + 節流計時器（避免 unmount 後仍 setState）
    return () => {
      if (unsubscribeRef.current) {
        console.log('[Admin useNotification] 🧹 Cleanup: Unsubscribing from NotificationChannel');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      // 切商家 / unmount 一併清掉 pending buffer，避免舊商家的 progress 殘留被下個訂閱 flush 進 notificationData
      pendingProgressRef.current = {};
    };
  }, [currentMerchant?.id, getMyJobs, flushPendingProgress]); // ✅ flushPendingProgress 為 stable useCallback 不會觸發 re-subscribe

  const clearJobStatus = useCallback((jobId) => {
    setNotificationData((prev) => {
      const newStatus = { ...prev };
      delete newStatus[jobId];
      return newStatus;
    });
  }, []);

  return {
    notificationData,  // ✅ 包含所有通知類型（job_status + new_notification）
    clearJobStatus,
    setModalVisible,  // ✅ 返回 setModalVisible 讓外部控制是否顯示通知
    registerJob       // ✅ [新增] 匯出註冊函數
  };
}

/**
 * Admin 專用的單一 Job 監聽 Hook
 *
 * @param {string|number} jobId - Job ID
 * @param {Function} onCompleted - 完成時的回調
 * @param {Function} onFailed - 失敗時的回調
 *
 * 使用範例：
 * ```javascript
 * const [jobId, setJobId] = useState(null);
 *
 * useSingleNotification(jobId, {
 *   onCompleted: (data) => {
 *     message.success('匯入完成');
 *     refetch(); // 重新載入資料
 *   },
 *   onFailed: (data) => {
 *     message.error(`匯入失敗: ${data.message}`);
 *   }
 * });
 * ```
 */
export function useSingleNotification(jobId, { onCompleted, onFailed, onProgress } = {}) {
  const { currentMerchant, loading } = useMerchant();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!jobId || !currentMerchant?.id) {
      return;
    }

    console.log('[Admin useSingleNotification] 🚀 Subscribing for jobId:', jobId);

    // ✅ 不需要 role 參數
    const unsubscribe = adminCable.subscribe(
      'NotificationChannel',
      { merchant_id: currentMerchant.id },
      {
        received: (data) => {
          // 只處理 job_status 類型的訊息
          if (data.type !== 'job_status') return;

          // 只處理匹配的 job_id
          if (data.job_id !== jobId && data.job_id !== String(jobId)) return;

          console.log('[Admin useSingleNotification] ✅ Received data for jobId:', jobId, data);
          setStatus(data);

          switch (data.status) {
            case 'completed':
              if (onCompleted) onCompleted(data);
              break;
            case 'failed':
              if (onFailed) onFailed(data);
              break;
            case 'processing':
              if (onProgress) onProgress(data);
              break;
          }
        }
      }
    );

    // ✅ Cleanup：組件 unmount 時清理訂閱
    return () => {
      console.log('[Admin useSingleNotification] 🧹 Cleanup: Unsubscribing for jobId:', jobId);
      unsubscribe();
    };
  }, [jobId, currentMerchant?.id, onCompleted, onFailed, onProgress]);

  return status;
}
